import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Divider,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useRecurringInvoiceQcRequests } from "src/hooks/recurring-invoice-qc/useRecurringInvoiceQcRequests";
import StatusChip from "../general/StatusChip";
import { RecurringInvoiceQc } from "models/recurring-invoice-qc";

const RecurringInvoiceQCs: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(
    generateCurrentMonthValue()
  );
  const {
    recurringInvoiceQcRequests,
    loading,
    fetchRecurringInvoiceQcRequestsByMonth,
  } = useRecurringInvoiceQcRequests();
  const navigate = useNavigate();

  const months = generateMonths();

  useEffect(() => {
    fetchRecurringInvoiceQcRequestsByMonth(selectedMonth);
  }, [selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const groupRecordsByDate = (
    records: RecurringInvoiceQc[]
  ): { [date: string]: RecurringInvoiceQc[] } => {
    return records.reduce((groups, record) => {
      const date = record.createdAt.substring(0, 10); // Group by date (YYYY-MM-DD)
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(record);
      return groups;
    }, {} as { [date: string]: RecurringInvoiceQc[] });
  };

  const groupedRecords = groupRecordsByDate(recurringInvoiceQcRequests);

  return (
    <Container sx={{ pt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <IconButton
          onClick={() => navigate("/")}
          size="small"
          sx={{ alignSelf: "flex-start", flexGrow: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1, textAlign: "center" }}>
          Recurring Invoice QC
        </Typography>
        <Box sx={{ flexGrow: 0, width: "48px" }} />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <Typography variant="h6" mr={2}>
          Select Month:
        </Typography>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
          inputProps={{ "aria-label": "Select Month" }}
        >
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" gutterBottom>
        Recurring Invoice QC Records
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : Object.keys(groupedRecords).length === 0 ? (
        <Typography>No recurring invoice QC records found</Typography>
      ) : (
        <Box>
          {Object.keys(groupedRecords).map((date) => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              {groupedRecords[date].map((record) => (
                <Box
                  key={record.requestId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    p: 1.5,
                    borderBottom: "1px solid #ccc",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() =>
                    navigate(
                      `/recurring-invoice-qc/RECINVQC-${record.requestId}`
                    )
                  }
                >
                  <Box sx={{ flex: "1 1 16%", minWidth: "150px" }}>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      RECINVQC-{record.requestId}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 20%",
                      display: "flex",
                      justifyContent: "center",
                      minWidth: "140px",
                    }}
                  >
                    <Chip
                      label={`Invoice ID: ${
                        record.input.tranId || record.input.invoiceId
                      }`}
                      color="default"
                      sx={{
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflow: "visible",
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 32%",
                      display: "flex",
                      justifyContent: "center",
                      minWidth: "220px",
                    }}
                  >
                    <Chip
                      label={record.output.decision}
                      color={
                        record.output.decision === "PASS"
                          ? "success"
                          : record.output.decision === "ERROR"
                          ? "error"
                          : "warning"
                      }
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: "4px 8px",
                          lineHeight: 1.4,
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 8%",
                      display: "flex",
                      justifyContent: "center",
                      minWidth: "100px",
                    }}
                  >
                    <StatusChip
                      status={record.status}
                      sx={{
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflow: "visible",
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 12%",
                      display: "flex",
                      justifyContent: "flex-end",
                      minWidth: "110px",
                    }}
                  >
                    <Typography variant="body2">
                      {new Date(record.createdAt).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

// Helper function to generate the current month value in "YYYY-MM" format
const generateCurrentMonthValue = (): string => {
  return new Date().toISOString().substring(0, 7);
};

// Helper function to generate months from 08/2024 to the current month
const generateMonths = (): { value: string; label: string }[] => {
  const startMonth = new Date(2024, 7, 2, 12, 0, 0); // August 2024
  const currentMonth = new Date();
  const months = [];

  for (
    let dt = new Date(startMonth);
    dt <= currentMonth;
    dt.setMonth(dt.getMonth() + 1)
  ) {
    const value = dt.toISOString().substring(0, 7);
    const label = dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    months.push({ value, label });
  }

  return months.reverse(); // Reverse to have the latest month first
};

export default RecurringInvoiceQCs;
